import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { NestedPick, Paginated, PaginationQueryWithFields } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { MaintenanceType, UpsertMaintenanceType } from "./types";

export const useMaintenanceTypesApi = () => {
  const api = useApi();

  const getMaintenanceType = useCallback(
    (maintenanceTypeId: string, signal: AbortSignal) => {
      const url = `/admin/maintenance_types/${maintenanceTypeId}`;
      return useSWRMutation<MaintenanceType | null, CustomError, string>(url, (url) =>
        api.get<MaintenanceType>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminMaintenanceTypes = useCallback(
    <K extends keyof MaintenanceType>(
      query: PaginationQueryWithFields<NestedPick<MaintenanceType, K>>,
      signal: AbortSignal,
    ) => {
      const url = buildUrl<NestedPick<MaintenanceType, K>>("/admin/maintenance_types", query);
      return useSWR<Paginated<NestedPick<MaintenanceType, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<MaintenanceType, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminMaintenanceType = useCallback(
    async (body: UpsertMaintenanceType): Promise<{ id: string }> => {
      const url = `/admin/maintenance_types`;
      const id = await api.post<UpsertMaintenanceType, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminMaintenanceType = useCallback(
    async (id: string, body: UpsertMaintenanceType) => {
      const url = `/admin/maintenance_types/${id}`;
      await api.put<UpsertMaintenanceType>(url, body);
      api.mutateCache([`/admin/maintenance_types`, `/admin/maintenances`]);
    },
    [api],
  );

  const deleteAdminMaintenanceType = useCallback(
    async (maintenanceTypeId: string): Promise<void> => {
      const url = `/admin/maintenance_types/${maintenanceTypeId}`;
      await api.del(url);
      api.mutateCache([`/admin/maintenance_types`]);
    },
    [api],
  );

  return {
    deleteAdminMaintenanceType,
    updateAdminMaintenanceType,
    insertAdminMaintenanceType,
    getMaintenanceType,
    useAdminMaintenanceTypes,
  };
};
